// Admin: Email Express block-driven section-treatment device library
// (Task #1894). Lists the curated devices, previews the fully COMPOSED prompt
// text (the code-side stitching constraints included), toggles enabled state,
// edits authored rows, and kicks the Sim-assisted candidate generator whose
// proposals land here as disabled drafts.
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, ChevronDown, ChevronUp, Lock } from "lucide-react";

interface DeviceRow {
  id: string;
  key: string;
  name: string;
  promptTemplate: string;
  enabled: boolean;
  sortOrder: number;
  source: string;
  rationale: string | null;
  isLegacy: boolean;
  composedPreview: string;
}

export default function EmailSectionDevicesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftTemplate, setDraftTemplate] = useState("");
  const [genJobId, setGenJobId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading } = useQuery<{ devices: DeviceRow[] }>({
    queryKey: ["/api/admin/email-section-devices"],
  });
  const devices = data?.devices ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["/api/admin/email-section-devices"] });

  const patch = useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const res = await apiRequest("PATCH", `/api/admin/email-section-devices/${id}`, body);
      return res.json();
    },
    onSuccess: () => invalidate(),
    onError: (e: any) =>
      toast({ title: "Update failed", description: e?.message, variant: "destructive" }),
  });

  const generate = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-section-devices/generate", {});
      return res.json();
    },
    onSuccess: (d: any) => setGenJobId(d.jobId),
    onError: (e: any) =>
      toast({
        title: "Could not start generation",
        description: e?.message,
        variant: "destructive",
      }),
  });

  // Poll the generation job until it settles.
  useEffect(() => {
    if (!genJobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `/api/admin/email-section-devices/generate/${genJobId}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const job = await res.json();
        if (job.status === "completed") {
          clearInterval(pollRef.current!);
          setGenJobId(null);
          invalidate();
          toast({
            title: "Candidates generated",
            description: `${job.created?.length ?? 0} draft device(s) added — review and enable them below.`,
          });
        } else if (job.status === "failed") {
          clearInterval(pollRef.current!);
          setGenJobId(null);
          toast({ title: "Generation failed", description: job.error, variant: "destructive" });
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genJobId]);

  const startEdit = (d: DeviceRow) => {
    setEditing(d.id);
    setDraftName(d.name);
    setDraftTemplate(d.promptTemplate);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-medium">Email Section Devices</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Section-treatment visual languages for block-driven Email Express
            polish renders. Only enabled rows are used; selection is
            deterministic per archetype. Stitching constraints (seam bands,
            side margin, no mascots) are appended in code and cannot be
            removed by editing a row.
          </p>
        </div>
        <Button
          onClick={() => generate.mutate()}
          disabled={generate.isPending || !!genJobId}
          data-testid="button-generate-device-candidates"
        >
          {genJobId || generate.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {genJobId ? "Generating…" : "Generate candidates"}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((d) => (
            <Card key={d.id} data-testid={`card-section-device-${d.key}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium">{d.name}</span>
                  <code className="text-xs text-muted-foreground">{d.key}</code>
                  <Badge variant={d.source === "sim" ? "default" : "secondary"}>
                    {d.source}
                  </Badge>
                  {d.isLegacy && (
                    <Badge variant="outline" className="gap-1">
                      <Lock className="h-3 w-3" /> pinned
                    </Badge>
                  )}
                  {!d.enabled && <Badge variant="outline">draft / disabled</Badge>}
                  <div className="ml-auto flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">Enabled</span>
                    <Switch
                      checked={d.enabled}
                      onCheckedChange={(v) => patch.mutate({ id: d.id, enabled: v })}
                      data-testid={`switch-device-enabled-${d.key}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                      data-testid={`button-device-expand-${d.key}`}
                    >
                      {expanded === d.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {d.rationale && (
                  <p className="text-xs text-muted-foreground italic">{d.rationale}</p>
                )}

                {expanded === d.id && (
                  <div className="space-y-3">
                    {editing === d.id ? (
                      <div className="space-y-2">
                        <Input
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          placeholder="Device name"
                          data-testid={`input-device-name-${d.key}`}
                        />
                        <Textarea
                          value={draftTemplate}
                          onChange={(e) => setDraftTemplate(e.target.value)}
                          rows={6}
                          className="font-mono text-xs"
                          disabled={d.isLegacy}
                          data-testid={`textarea-device-template-${d.key}`}
                        />
                        <p className="text-xs text-muted-foreground">
                          Placeholders: {"{{backgroundHex}}"} and {"{{secondaryAccentHex}}"}.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={patch.isPending}
                            onClick={() =>
                              patch.mutate(
                                {
                                  id: d.id,
                                  name: draftName,
                                  ...(d.isLegacy ? {} : { promptTemplate: draftTemplate }),
                                },
                                { onSuccess: () => setEditing(null) },
                              )
                            }
                            data-testid={`button-device-save-${d.key}`}
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <p className="text-xs font-medium mb-1">
                            Composed prompt (what render time uses):
                          </p>
                          <pre className="text-xs whitespace-pre-wrap bg-muted rounded p-3 max-h-64 overflow-auto">
                            {d.composedPreview}
                          </pre>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(d)}
                          data-testid={`button-device-edit-${d.key}`}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {!devices.length && (
            <p className="text-sm text-muted-foreground">
              No devices found — the seed runs at server boot.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
